import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import SideNav from '../../common/SideNav';
import {
    hasSideBar,
    toggleSideBar,
    updateCurrentComponent,
} from '../../../store/actions/navActions';
import { faqList } from './FaqList';
import * as C from '../../../services/constants';

import chevronDownIcon from '../../../resources/images/chevron-down.svg';
import chevronUpIcon from '../../../resources/images/chevron-up.svg';

const Help = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const openGoalEditor = () => {
        navigate('/nba2.0-dashboard', { state: { openGoalEditor: true } });
    };

    const [expandedItems, setExpandedItems] = useState([]);
    const [allExpanded, setAllExpanded] = useState(false);

    useEffect(() => {
        dispatch(updateCurrentComponent(C.APP_HELP_PAGE_TITLE));
        dispatch(hasSideBar(true));
        dispatch(toggleSideBar(false));
    });

    const handleToggleAccordion = (index) => {
        let newExpandedItems = [...expandedItems];
        const currentIndex = newExpandedItems.indexOf(index);

        if (currentIndex === -1) {
            newExpandedItems.push(index);
        } else {
            newExpandedItems.splice(currentIndex, 1);
        }

        setExpandedItems(newExpandedItems);
        setAllExpanded(newExpandedItems.length === faqList.length);
    };

    const handleCollapseAll = () => {
        setExpandedItems([]);
        setAllExpanded(false);
    };

    const handleExpandAll = () => {
        const allIndexes = faqList.map((_, index) => index);
        setExpandedItems(allIndexes);
        setAllExpanded(true);
    };

    return (
        <div className="component-container-root">
            <SideNav openGoalEditor={openGoalEditor}/>
            <div className="help-container" style={{marginBottom:"5px"}}>
                 <h2 style={{marginBottom:"4px"}}>Market Configuration</h2>
                <p style={{marginBottom:"15px"}}>
                    The Market Configuration screen displays and manages all existing goals for the selected market within the NBA2.0 Program.
                </p>
                <p style={{marginBottom:"15px"}}>
                    You can view, edit, or deactivate goal configurations as needed. Each row in the table represents one goal.
                </p>
                <p style={{marginBottom:"15px"}}>
                    The Add Goal screen allows you to create and configure a new goal for the NBA2.0 Program for each market.
                    Each goal defines a measurable objective with a specific weight, which determines its relative importance for that market.
                </p>
                <ul>
                    <li>Up-sell</li>
                    <li>Cross-sell</li>
                    <li>Loyalty</li>
                    <li>Repeat Purchase</li>
                </ul>

                <h3 style={{marginBottom:"5px"}}>Field Descriptions</h3>
                <ul >
                    <li style={{marginBottom:"2px"}}>
                        <strong>Goal Type*:</strong> Select the type of goal you want to create from the dropdown (e.g., Up-sell, Cross-sell, Loyalty & Repeat Purchase). This defines the strategic intent of the goal.
                    </li>
                    <li style={{marginBottom:"2px"}}>
                        <strong>Goal Name*:</strong> Enter a descriptive name for the goal (e.g., Up-sell Goal, Cross-sell More). The name should clearly identify the goal's purpose.
                    </li>
                    <li style={{marginBottom:"2px"}}>
                        <strong>Weight*:</strong> Enter a numeric value representing the proportion or importance of this goal in the overall model.
                        <ul style={{marginTop:"5px", marginBottom:"5px"}}>
                            <li>Accepted values: 0, 1, or decimal values between 0 and 1 eg. 0.5, 0.75 etc.</li>
                            <li>Example: A weight of 0.25 means this goal contributes to 25% of the total importance in the configuration.</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Save Goal Button:</strong> Click Save Goal after filling all mandatory fields to add this goal to the Market Configuration list.
                    </li>
                </ul>

                <h3 style={{marginBottom:"5px"}}>Tips</h3>
                <ul>
                    <li>The total weight across all active goals can add up to maximum value of 1 based on your business rules.</li>
                    <li>Choose meaningful names that help others easily identify the goal's purpose.</li>
                    <li>You can manage or edit created goals from the Market Configuration screen.</li>
                    <li>If for a market we define only one goal; example cross sell is defined with a weightage of 0.4, the remaining 0.6, will be equally divided implicitly between the other 3 goals (upsell, Loyalty and repeat purchase).</li>
                    <li>If NO goal is defined for a market then the total weightage is divided equally across all the goals; example each goal upsell, Loyalty and repeat purchase and Cross sell will have a weightage of 0.25</li>
                </ul>

                <div className="faq-header">
                    <h2 style={{marginBottom:"5px"}}>Frequently Asked Questions</h2>
                    <button
                        className="faq-collapse-all"
                        onClick={
                            allExpanded ? handleCollapseAll : handleExpandAll
                        }
                    >
                        <img
                            src={allExpanded ? chevronUpIcon : chevronDownIcon}
                            alt="collapse all"
                        />{' '}
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                    </button>
                </div>
                <div style={{ width: '70%' }}>
                    {faqList.map((faq, index) => (
                        <Accordion
                            key={index}
                            expanded={expandedItems.includes(index)}
                            onChange={() => handleToggleAccordion(index)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <h3>{faq.question}</h3>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ul>
                                    {faq.answer.map((ans, idx) => (
                                        <li
                                            key={idx}
                                            dangerouslySetInnerHTML={{
                                                __html: ans,
                                            }}
                                        />
                                    ))}
                                </ul>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Help;
